import React, { FC, useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { DragDropContext, Droppable, DropResult, Draggable } from 'react-beautiful-dnd';
import { Box, Button, IconButton, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import AddIcon from '@mui/icons-material/Add';
import ArrowLeftIcon from '@mui/icons-material/ArrowLeft';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { QuestionSlim, QuizWithQuestions } from '@/app2/shared/data';
import { palette } from '@/constants';
import EditQuizQuestionModal from './EditQuizQuestionModal';

/** Prod-style drag affordance: two horizontal lines */
const DoubleLineDragHandle: FC<{ className?: string }> = ({ className }) => (
	<Box
		className={className}
		aria-hidden
		sx={{
			display: 'flex',
			flexDirection: 'column',
			justifyContent: 'center',
			gap: '4px',
			width: 18,
			height: 20
		}}
	>
		<Box sx={{ height: 2, borderRadius: 0.5, bgcolor: 'rgba(255,255,255,0.75)' }} />
		<Box sx={{ height: 2, borderRadius: 0.5, bgcolor: 'rgba(255,255,255,0.75)' }} />
	</Box>
);

export interface IQuizBuilderQuestion extends QuestionSlim {
	dragId: string;
}

export interface AdminQuizBuilderProps {
	quiz: QuizWithQuestions;
	/** Order to restore on Reset (hydrated / last save / after add questions). */
	compositionResetBaseline: QuestionSlim[];
	onChange: (composition: QuestionSlim[]) => void;
	onQuestionAdd?: () => void;
}

const mapToQuizBuilderQuestion = (q: QuestionSlim): IQuizBuilderQuestion => ({
	...q,
	dragId: `${q.__type}-${q.id}`
});

const moveQuestion = (
	questions: IQuizBuilderQuestion[],
	sourceIndex: number,
	destinationIndex: number
): IQuizBuilderQuestion[] => {
	const result = [...questions];
	const [removed] = result.splice(sourceIndex, 1);
	result.splice(destinationIndex, 0, removed);
	return result;
};

const cloneList = (list: IQuizBuilderQuestion[]): IQuizBuilderQuestion[] =>
	JSON.parse(JSON.stringify(list));

const orderKey = (list: IQuizBuilderQuestion[]) => list.map((q) => q.dragId).join('|');

const multisetKey = (list: IQuizBuilderQuestion[]) =>
	[...list]
		.map((q) => q.dragId)
		.sort()
		.join('|');

const formatQuestionType = (type: string): string =>
	type
		.replace(/_/g, ' ')
		.replace(/questions/gi, '')
		.replace(/question/gi, '')
		.trim() || 'Question';

const useStyles = makeStyles()((theme) => ({
	toolbar: {
		display: 'flex',
		zIndex: 1,
		top: 0,
		background: palette.drawer,
		gap: theme.spacing(1.25),
		padding: '20px 32px',
		minHeight: 56,
		position: 'sticky',
		justifyContent: 'space-between',
		alignItems: 'center',
		borderRadius: theme.shape.borderRadius,
		marginBottom: theme.spacing(3),
		flexWrap: 'wrap',
		border: `1px solid ${palette.greyBorder}`
	},
	toolbarGroup: {
		display: 'flex',
		gap: theme.spacing(1.25),
		alignItems: 'center',
		flexWrap: 'wrap'
	},
	toolbarCluster: {
		display: 'flex',
		gap: theme.spacing(1.25)
	},
	toolbarOutlinedBtn: {
		height: 28,
		minWidth: 0,
		padding: theme.spacing(0.75, 1.25),
		fontSize: '0.75rem',
		fontWeight: 700,
		textTransform: 'none',
		borderRadius: '5px',
		border: `1px solid ${palette.orange} !important`,
		backgroundColor: 'transparent',
		color: `${palette.white} !important`,
		'&:hover': {
			backgroundColor: palette.orange08,
			borderColor: `${palette.orange} !important`
		},
		'&.Mui-disabled': {
			border: '1px solid rgba(255, 158, 0, 0.4) !important',
			color: 'rgba(255, 255, 255, 0.4) !important',
			opacity: 1
		},
		'& .MuiSvgIcon-root': {
			fontSize: '1.1rem',
			color: `${palette.white} !important`,
			marginRight: theme.spacing(0.5),
			opacity: 1
		},
		'&.Mui-disabled .MuiSvgIcon-root': {
			color: 'rgba(255, 255, 255, 0.35) !important'
		}
	},
	questionContainer: {
		padding: theme.spacing(0, 3, 3)
	},
	questionRow: {
		position: 'relative',
		display: 'flex',
		alignItems: 'center',
		borderRadius: '4px',
		backgroundColor: palette.drawer,
		border: `1px solid ${palette.greyBorder}`,
		gap: theme.spacing(0.5),
		marginBottom: theme.spacing(1),
		maxHeight: 80,
		cursor: 'pointer',
		transition: theme.transitions.create(['opacity', 'box-shadow'], {
			duration: theme.transitions.duration.shortest
		}),
		'& > *': {
			paddingTop: theme.spacing(2.5),
			paddingBottom: theme.spacing(2.5),
			paddingLeft: theme.spacing(1.25)
		}
	},
	dragHandle: {
		display: 'flex',
		alignItems: 'center',
		cursor: 'grab',
		paddingLeft: `${theme.spacing(1)} !important`
	},
	questionLine: {
		flexGrow: 1,
		minWidth: 0,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		paddingLeft: '0 !important',
		textTransform: 'none',
		color: palette.white,
		fontWeight: 500
	},
	typePrefix: {
		color: 'rgba(255, 255, 255, 0.72)',
		fontWeight: 500,
		marginRight: theme.spacing(1)
	},
	deleteWrap: {
		display: 'flex',
		alignItems: 'center',
		paddingRight: `${theme.spacing(2.5)} !important`,
		'& .MuiIconButton-root': {
			color: 'rgba(255, 255, 255, 0.45)',
			opacity: 1,
			transition: theme.transitions.create(['color', 'transform'], {
				duration: theme.transitions.duration.shortest
			}),
			'&:hover': {
				color: palette.white
			}
		}
	},
	emptyState: {
		padding: theme.spacing(5),
		textAlign: 'center',
		color: theme.palette.text.secondary
	}
}));

const AdminQuizBuilder: FC<AdminQuizBuilderProps> = ({
	quiz,
	compositionResetBaseline,
	onChange,
	onQuestionAdd
}) => {
	const { classes } = useStyles();
	const [questionList, setQuestionList] = useState<IQuizBuilderQuestion[]>([]);
	const [past, setPast] = useState<IQuizBuilderQuestion[][]>([]);
	const [future, setFuture] = useState<IQuizBuilderQuestion[][]>([]);
	const [editingQuestion, setEditingQuestion] = useState<IQuizBuilderQuestion | null>(null);
	const skipNextOnChange = useRef(false);
	const questionListRef = useRef<IQuizBuilderQuestion[]>([]);
	const lastQuizIdRef = useRef<number | undefined>(undefined);
	questionListRef.current = questionList;

	const stableQuizId = Number(quiz.id);
	const questionDetailsByKey = useMemo(() => {
		const details = new Map<string, QuestionSlim>();
		Object.values(quiz.questions ?? {}).flat().forEach((question) => {
			details.set(`${question.__type}-${question.id}`, question);
		});
		return details;
	}, [quiz.questions]);
	const compositionSyncKey = useMemo(
		() =>
			(quiz.composition ?? [])
				.map((q) => `${(q as QuestionSlim).__type}-${(q as QuestionSlim).id}`)
				.join('|'),
		[quiz.composition]
	);

	const hydrateQuestion = useCallback((question: QuestionSlim): QuestionSlim => {
		const details = questionDetailsByKey.get(`${question.__type}-${question.id}`);
		if (!details) {
			return question;
		}
		return {
			...details,
			...question,
			title: question.title || details.title
		};
	}, [questionDetailsByKey]);

	const pushPast = (before: IQuizBuilderQuestion[]) => {
		setPast((p) => [...p, cloneList(before)]);
		setFuture([]);
	};

	useEffect(() => {
		const incoming = (quiz.composition ?? []).map(hydrateQuestion).map(mapToQuizBuilderQuestion);

		if (lastQuizIdRef.current !== stableQuizId) {
			lastQuizIdRef.current = stableQuizId;
			skipNextOnChange.current = true;
			setQuestionList(incoming);
			setPast([]);
			setFuture([]);
			return;
		}

		const prev = questionListRef.current;
		if (orderKey(incoming) === orderKey(prev)) {
			return;
		}

		if (multisetKey(incoming) === multisetKey(prev) && orderKey(incoming) !== orderKey(prev)) {
			return;
		}

		skipNextOnChange.current = true;
		setQuestionList(incoming);
		setPast([]);
		setFuture([]);
	}, [stableQuizId, compositionSyncKey, hydrateQuestion]);

	useEffect(() => {
		if (skipNextOnChange.current) {
			skipNextOnChange.current = false;
			return;
		}
		// Never emit a transient empty list back to parent while incoming props still contain
		// questions; this can happen during remount/effect timing and would wipe form state.
		if (questionList.length === 0 && (quiz.composition?.length ?? 0) > 0) {
			return;
		}
		// Prevent mount/sync echo from writing back into parent form state.
		// In dev strict-mode and route remounts, local list can transiently be [] before
		// sync settles; emitting that would incorrectly clear form composition.
		const incomingFromProps = (quiz.composition ?? []).map(hydrateQuestion).map(mapToQuizBuilderQuestion);
		if (orderKey(questionList) === orderKey(incomingFromProps)) {
			return;
		}
		onChange(questionList.map(({ dragId, ...rest }) => rest));
	}, [questionList, onChange, quiz.composition, hydrateQuestion]);

	const handleDragEnd = (result: DropResult): void => {
		const { source, destination } = result;
		if (!destination || source.index === destination.index) {
			return;
		}
		const before = cloneList(questionList);
		setQuestionList(moveQuestion(questionList, source.index, destination.index));
		pushPast(before);
	};

	const handleDelete = (index: number): void => {
		const before = cloneList(questionList);
		setQuestionList((prev) => {
			const next = [...prev];
			next.splice(index, 1);
			return next;
		});
		pushPast(before);
	};

	const handleQuestionSaved = (updatedQuestion: QuestionSlim): void => {
		const next = questionListRef.current.map((question) =>
			question.__type === updatedQuestion.__type && question.id === updatedQuestion.id
				? mapToQuizBuilderQuestion(updatedQuestion)
				: question
		);
		setQuestionList(next);
		onChange(next.map(({ dragId, ...rest }) => rest));
	};

	const handleQuestionRowKeyDown = (
		event: React.KeyboardEvent<HTMLElement>,
		question: IQuizBuilderQuestion
	): void => {
		if (event.target !== event.currentTarget) {
			return;
		}
		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault();
			setEditingQuestion(question);
		}
	};

	const handleUndo = () => {
		if (past.length === 0) return;
		const previous = past[past.length - 1];
		setPast((p) => p.slice(0, -1));
		setFuture((f) => [cloneList(questionList), ...f]);
		setQuestionList(previous);
	};

	const handleRedo = () => {
		if (future.length === 0) return;
		const next = future[0];
		setFuture((f) => f.slice(1));
		setPast((p) => [...p, cloneList(questionList)]);
		setQuestionList(next);
	};

	const enableUndo = past.length > 0;
	const enableRedo = future.length > 0;
	const enableReset = past.length > 0 || future.length > 0;

	const handleReset = () => {
		if (past.length === 0 && future.length === 0) return;
		const initial = (compositionResetBaseline ?? []).map(mapToQuizBuilderQuestion);
		setQuestionList(initial);
		setPast([]);
		setFuture([]);
	};

	const toolbar = onQuestionAdd ? (
		<Box className={classes.toolbar}>
			<Box className={classes.toolbarGroup}>
				<Button
					className={classes.toolbarOutlinedBtn}
					variant="outlined"
					size="small"
					startIcon={<AddIcon />}
					onClick={() => onQuestionAdd()}
					data-test-id="quiz-builder-controls-add-question-btn"
				>
					Questions
				</Button>
			</Box>
			<Box className={`${classes.toolbarGroup} ${classes.toolbarCluster}`}>
				<Button
					className={classes.toolbarOutlinedBtn}
					variant="outlined"
					size="small"
					startIcon={<ArrowLeftIcon />}
					disabled={!enableUndo}
					onClick={handleUndo}
					data-test-id="quiz-builder-controls-undo-btn"
				>
					Undo
				</Button>
				<Button
					className={classes.toolbarOutlinedBtn}
					variant="outlined"
					size="small"
					startIcon={<ArrowRightIcon />}
					disabled={!enableRedo}
					onClick={handleRedo}
					data-test-id="quiz-builder-controls-redo-btn"
				>
					Redo
				</Button>
				<Button
					className={classes.toolbarOutlinedBtn}
					variant="outlined"
					size="small"
					startIcon={<RefreshIcon />}
					disabled={!enableReset}
					onClick={handleReset}
					data-test-id="quiz-builder-controls-reset-btn"
				>
					Reset
				</Button>
			</Box>
		</Box>
	) : null;

	if ((quiz.composition?.length ?? 0) === 0) {
		return (
			<Box>
				{toolbar}
				<Box className={classes.emptyState}>
					<Typography variant="body1">No questions added yet.</Typography>
					{onQuestionAdd && (
						<Typography variant="body2" sx={{ mt: 1 }}>
							Click &quot;Questions&quot; above to get started.
						</Typography>
					)}
				</Box>
			</Box>
		);
	}

	return (
		<Box>
			{toolbar}
			<EditQuizQuestionModal
				open={editingQuestion !== null}
				question={editingQuestion}
				onClose={() => setEditingQuestion(null)}
				onSaved={handleQuestionSaved}
			/>
			<DragDropContext onDragEnd={handleDragEnd}>
				<Droppable droppableId="quizBuilder" type="base">
					{(droppableProvided) => (
						<div {...droppableProvided.droppableProps} ref={droppableProvided.innerRef}>
							<Box className={classes.questionContainer}>
								{questionList.map((question, index) => (
									<Draggable
										key={question.dragId}
										draggableId={question.dragId}
										index={index}
									>
										{(draggableProvided, snapshot) => (
											<section
												ref={draggableProvided.innerRef}
												{...draggableProvided.draggableProps}
											>
												<Box
													className={classes.questionRow}
													role="button"
													tabIndex={0}
													onClick={() => setEditingQuestion(question)}
													onKeyDown={(event) => handleQuestionRowKeyDown(event, question)}
													style={{
														opacity: snapshot.isDragging ? 0.85 : 1,
														boxShadow: snapshot.isDragging
															? '0 4px 12px rgba(0,0,0,0.25)'
															: undefined
													}}
												>
													<Box
														className={classes.dragHandle}
														{...draggableProvided.dragHandleProps}
													>
														<DoubleLineDragHandle />
													</Box>
													<Typography className={classes.questionLine} variant="body1" component="div">
														<Box component="span" className={classes.typePrefix}>
															{formatQuestionType(question.__type)} :
														</Box>
														<Box component="span">{question.title || '—'}</Box>
													</Typography>
													<Box className={classes.deleteWrap}>
														<IconButton
															size="small"
															edge="end"
															onClick={(e) => {
																e.stopPropagation();
																handleDelete(index);
															}}
															aria-label="Delete question"
														>
															<DeleteOutlineIcon fontSize="small" />
														</IconButton>
													</Box>
												</Box>
											</section>
										)}
									</Draggable>
								))}
								{droppableProvided.placeholder}
							</Box>
						</div>
					)}
				</Droppable>
			</DragDropContext>
		</Box>
	);
};

export default AdminQuizBuilder;
